/**
 * Centralised TanStack Query keys. Co-locating these per-feature keeps the
 * cache invalidation calls in `useCreateSite`, `useUpdateSite`, etc. honest
 * — every mutation knows exactly which keys to invalidate without typo
 * risk.
 *
 * Convention (`standards/react-patterns.md`): keys are arrays starting with
 * a feature segment, then any IDs / params, and they are *as narrow as
 * possible* — `["sites", "list", { page: 1 }]` is preferred over
 * `["sites"]` so a single-site refresh doesn't refetch every list view.
 */

export const queryKeys = {
  auth: {
    me: ["auth", "me"] as const,
  },
  sites: {
    all: ["sites"] as const,
    list: (params: { page: number; pageSize: number }) => ["sites", "list", params] as const,
    detail: (id: string) => ["sites", "detail", id] as const,
  },
  pages: {
    all: ["pages"] as const,
    listForSite: (siteId: string, params: { page: number; pageSize: number }) =>
      ["pages", "site", siteId, "list", params] as const,
    /**
     * Single-page detail (issue #172). Returns `{ page, latestDraft,
     * published }`. Mutations on the PageVersion lifecycle endpoints
     * invalidate this key so the lifecycle slots re-render with the new
     * authoritative state.
     */
    detail: (id: string) => ["pages", "detail", id] as const,
  },
  domains: {
    all: ["domains"] as const,
    listForSite: (siteId: string) => ["domains", "site", siteId, "list"] as const,
  },
  contentItems: {
    all: ["contentItems"] as const,
    listForSite: (siteId: string, params: { page: number; pageSize: number }) =>
      ["contentItems", "site", siteId, "list", params] as const,
    /**
     * Single ContentItem detail (issue #173). Returns
     * `{ item, latestDraft, published, archived }`. Mutations on the
     * ContentVersion lifecycle endpoints invalidate this key so the
     * editor slots re-render against the authoritative state.
     */
    detail: (id: string) => ["contentItems", "detail", id] as const,
  },
  ai: {
    all: ["ai"] as const,
    usage: (month: string | undefined) => ["ai", "usage", month ?? "current"] as const,
    budgetStatus: ["ai", "budget-status"] as const,
  },
  leads: {
    all: ["leads"] as const,
    list: (params: Record<string, string | number | undefined>) =>
      ["leads", "list", params] as const,
    detail: (id: string) => ["leads", "detail", id] as const,
  },
  analytics: {
    all: ["analytics"] as const,
    dashboard: (params: { siteId: string; from: string; to: string }) =>
      ["analytics", "dashboard", params] as const,
  },
  experiments: {
    all: ["experiments"] as const,
    listForSite: (
      siteId: string,
      params: {
        page: number;
        pageSize: number;
        status?: string;
        /**
         * Issue #253: part of the query key so the "show archived"
         * checkbox toggles fetch a fresh page rather than reading from
         * the hidden-archived cache and silently lying.
         */
        includeArchived?: boolean;
      },
    ) => ["experiments", "site", siteId, "list", params] as const,
    detail: (id: string) => ["experiments", "detail", id] as const,
    /** Metrics query key (issue #27). Cached server-side too — 60s TTL. */
    metrics: (id: string) => ["experiments", "metrics", id] as const,
  },
} as const;
